import { useEffect, useRef, useState } from "react";
import {
  Clock,
  ImageIcon,
  LoaderCircle,
  Trash2,
  User,
  Users,
  X,
} from "lucide-react";
import { useSelector } from "react-redux";
import quickMessageApi from "../../../../api/quickMessageApi";
import otherApi from "../../../../api/otherApi";
import { useNotification } from "../../../../hooks/useNotification";

export default function AddQuickMessageModal({
  isOpen,
  setIsOpen,
  shop,
  editingQuickMessage,
  setEditingQuickMessage,
  getQuickMessages,
}) {
  const { addNotification } = useNotification();
  const auth = useSelector((state) => state.auth);
  const contentRef = useRef(null);
  const imagesRef = useRef(null);
  const [shortcut, setShortcut] = useState("");
  const [content, setContent] = useState("");
  const [images, setImages] = useState([]);
  const [day, setDay] = useState("TODAY");
  const [formatDay, setFormatDay] = useState("DD/MM/YYYY");
  const [isLoading, setIsLoading] = useState(false);

  const handleImageUpload = (e) => {
    if (e.target.files) {
      const newImages = Array.from(e.target.files).map((file) =>
        URL.createObjectURL(file)
      );
      setImages((prev) => [...prev, ...newImages]);
    }
  };

  const handleImageDelete = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveClick = async () => {
    let res;
    if (!shortcut.trim() || (!content.trim() && !images.length === 0)) {
      addNotification("Vui lòng điền đầy đủ thông tin", -1);
      return;
    }

    setIsLoading(true);
    const imageUrls = [];
    for (const file of Array.from(imagesRef.current.files)) {
      const resPostImage = await otherApi.postImage(shortcut, file);
      if (resPostImage.url) imageUrls.push(resPostImage.url);
    }

    if (editingQuickMessage) {
      res = await quickMessageApi.updateById({
        ...editingQuickMessage,
        shortcut,
        content,
        images: [
          ...images.filter((img) => img.includes("imagekit.io")),
          ...imageUrls,
        ],
      });
    } else {
      res = await quickMessageApi.create({
        accountId: auth.user.accountId,
        shopId: shop._id,
        shortcut,
        content,
        images: imageUrls,
      });
    }
    setIsLoading(false);
    if (res.status === 1) {
      await getQuickMessages();
      addNotification("Lưu thành công", res.status);
      handleCloseClick();
    } else addNotification(res.message, res.status);
  };

  const handleCloseClick = () => {
    setIsOpen(false);
    setEditingQuickMessage(null);
    setShortcut("");
    setContent("");
    setImages([]);
  };

  useEffect(() => {
    if (editingQuickMessage) {
      setShortcut(editingQuickMessage.shortcut);
      setContent(editingQuickMessage.content);
      setImages(editingQuickMessage.images);
    }
  }, [editingQuickMessage]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black text-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-lg w-full max-w-6xl flex h-[600px] relative">
        {/* Close button */}
        <button
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
          onClick={handleCloseClick}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Main content */}
        <div className="flex-1 p-6 border-r overflow-auto">
          <h2 className="text-xl font-semibold mb-4">Thêm câu trả lời nhanh</h2>

          {/* Shortcut input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ký tự tắt
            </label>
            <div className="flex items-center border rounded-md">
              <span className="text-gray-500 px-3">/</span>
              <input
                type="text"
                className="flex-1 p-2 outline-none"
                placeholder="Nhập nội dung ký tự"
                value={shortcut}
                onChange={(e) =>
                  setShortcut(e.target.value.replaceAll(" ", ""))
                }
              />
            </div>
          </div>

          {/* Content section */}
          <div className="mb-4">
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="mb-4">
                <span className="inline-flex items-center text-sm text-gray-600 mb-2">
                  Nội dung
                </span>
                <textarea
                  className="w-full p-3 border rounded-md resize-none"
                  rows={4}
                  placeholder="Nhập nội dung..."
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  ref={contentRef}
                />
              </div>

              {/* Multiple Image upload */}
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Hình ảnh
                </span>
                <div className="flex flex-wrap gap-4">
                  {images.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image || "/placeholder.svg"}
                        alt={`Uploaded ${index + 1}`}
                        className="w-32 h-32 object-cover rounded-md border"
                      />
                      <button
                        onClick={() => handleImageDelete(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <label className="border-2 border-dashed rounded-md p-4 w-32 h-32 flex flex-col items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer">
                    <ImageIcon className="w-6 h-6 mb-2" />
                    <span className="text-sm text-blue-500">Tải ảnh</span>
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      ref={imagesRef}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button
              className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50"
              onClick={handleCloseClick}
            >
              Đóng
            </button>
            <button
              className="flex px-4 py-2 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600"
              onClick={handleSaveClick}
              disabled={isLoading}
            >
              {isLoading && (
                <LoaderCircle
                  className="animate-spin text-white mr-2"
                  width={20}
                  height={20}
                />
              )}
              Lưu
            </button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-90 p-6 bg-gray-50 overflow-auto">
          <h3 className="text-sm font-medium text-blue-500 mb-4">
            Chèn thông tin
          </h3>

          <div className="mb-4">
            <h4 className="flex items-center text-sm font-medium text-gray-700 mb-4">
              <User className="w-4 h-4 mr-2" />
              Khách hàng
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Tên hiển thị Zalo
                </label>
                <button
                  className="p-2 bg-white rounded-md text-sm border"
                  onClick={() => {
                    setContent(content + "#{FULL_NAME} ");
                    contentRef.current.focus();
                  }}
                >
                  #{"{FULL_NAME}"}
                </button>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Tên gốc Zalo
                </label>
                <button
                  className="p-2 bg-white rounded-md text-sm border"
                  onClick={() => {
                    setContent(content + "#{ORIGINAL_NAME} ");
                    contentRef.current.focus();
                  }}
                >
                  #{"{ORIGINAL_NAME}"}
                </button>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Giới tính
                </label>
                <button
                  className="p-2 bg-white rounded-md text-sm border"
                  onClick={() => {
                    setContent(content + "#{SEX{MALE|FEMALE|UNKNOWN}} ");
                    contentRef.current.focus();
                  }}
                >
                  #{"{SEX{MALE|FEMALE|UNKNOWN}}"}
                </button>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h4 className="flex items-center text-sm font-medium text-gray-700 mb-4">
              <Users className="w-4 h-4 mr-2" />
              Nhân viên
            </h4>
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Tên nhân viên trả lời
              </label>
              <button
                className="p-2 bg-white rounded-md text-sm border"
                onClick={() => {
                  setContent(content + "#{STAFF_NAME} ");
                  contentRef.current.focus();
                }}
              >
                #{"{STAFF_NAME}"}
              </button>
            </div>
          </div>

          <div>
            <h4 className="flex items-center text-sm font-medium text-gray-700 mb-4">
              <Clock className="w-4 h-4 mr-2" />
              Chọn thời gian
            </h4>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Chọn ngày:
                </label>
                <select
                  className="p-2 text-sm border rounded-md w-full bg-white"
                  value={day}
                  onChange={(e) => setDay(e.target.value)}
                >
                  <option value="TODAY">Hôm nay</option>
                  <option value="TOMORROW">Ngày mai</option>
                  <option value="YESTERDAY">Hôm qua</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Chọn định dạng:
                </label>
                <select
                  className="p-2 text-sm border rounded-md w-full bg-white"
                  value={formatDay}
                  onChange={(e) => setFormatDay(e.target.value)}
                >
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Chọn định dạng
              </label>
              <button
                className="p-2 bg-white rounded-md text-sm border"
                onClick={() => {
                  setContent(content + `#{${day}(${formatDay})} `);
                  contentRef.current.focus();
                }}
              >
                #{`{${day}(${formatDay})}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
